import GetHierarchyBody from './HierarchyBody';

const flattenBody = (body) => {
  return body ? body.filter((item) => !!item) : [];
};

const computeLayoutItemList = ({
  friendlyName,
  layoutItemName,
  currentItem,
  path,
  pathPostFix,
  blocks,
  getMergedTemplate,
  currentOverrides,
  jsxComponents,
  sortComponents,
}) => {
  const body = [];
  sortComponents(currentItem).filter(([_key, config]) => !!config?.LayoutItem && !config?.Hidden).forEach(([configKey, config]) => {
    const child = config.LayoutItem;

    const nestedBody = GetHierarchyBody({
      path: [...path, layoutItemName, configKey, ...pathPostFix],
      blocks,
      getMergedTemplate,
      currentOverrides,
      jsxComponents,
      sortComponents,
    });

    body.push({
      name: `${friendlyName || layoutItemName}.${config.Name || child.name}`,
      config: child.config,
      path: [...path, layoutItemName, configKey, ...pathPostFix],
      isRoot: false,
      isBlock: !!blocks[child.name],
      isLayoutItem: true,
      layoutItemName: child.name,
      body: flattenBody(nestedBody),
    });
  });

  return body.length > 0 ? flattenBody(body) : [];
};

const computeLayoutItem = ({
  friendlyName,
  name,
  currentItem,
  path,
  pathPostFix,
  blocks,
  getMergedTemplate,
  currentOverrides,
  jsxComponents,
  sortComponents,
}) => {
  let body = GetHierarchyBody({
    path: [...path, name, ...pathPostFix],
    blocks,
    getMergedTemplate,
    currentOverrides,
    jsxComponents,
    sortComponents,
  });

  return [{
    name: `${friendlyName}.${currentItem.friendlyName || currentItem.name}`,
    config: currentItem.config,
    path: [...path, name, ...pathPostFix],
    isRoot: false,
    isBlock: !!blocks[currentItem.name],
    isLayoutItem: true,
    layoutItemName: currentItem.name,
    body: body ? body.filter((item) => !!item) : [],
  }];
};

const computeObject = ({
  friendlyName,
  name,
  keys,
  currentItem,
  currentPath,
  renderCallback
}) => {
  var body = [];
  keys.forEach((key) => {
    body.push(...renderCallback({
      ...key,
      currentItem: currentItem[key.name],
      currentPath,
    }));
  });

  body = flattenBody(body);

  if (body.length > 0) {
    return [{
      name: friendlyName || name,
      path: [],
      config: {},
      isRoot: false,
      isBlock: false,
      isLayoutItem: false,
      body,
    }];
  }

  return [];
};

const computeSortableObject = ({
  name,
  keys,
  currentItem,
  currentPath,
  renderCallback,
  sortComponents,
}) => {
  const components = sortComponents(currentItem).filter(([_order, config]) => config && !config.Hidden).map(([order, config]) => ([order, config]));

  let body = [];
  components.forEach((component) => {
    var nestedBody = [];
    keys.forEach((key) => nestedBody.push(...renderCallback({
      ...key,
      currentItem: component[1][key.name],
      currentPath: [...currentPath, component[0]],
    })));

    nestedBody = flattenBody(nestedBody);

    body.push({
      name: `${name}.${component[0]}`,
      path: [],
      config: {},
      isRoot: false,
      isBlock: false,
      isLayoutItem: false,
      body: nestedBody,
    });
  });

  body = flattenBody(body);

  if (body.length > 0) {
    return body;
  }

  return [];
};

const GetHierarchyParams = ({
  componentMetaData,
  layoutItem,
  path,
  sortComponents,
  blocks,
  getMergedTemplate,
  currentOverrides,
  jsxComponents,
}) => {
  const computeChildren = ({ name, friendlyName, type, keys, currentItem, currentPath, defaultValue }) => {
    if (!currentItem) {
      return [];
    }

    switch (type) {
      case 'LayoutItemList': {
        return computeLayoutItemList({
          friendlyName,
          layoutItemName: name,
          currentItem,
          path: [...currentPath],
          pathPostFix: ['LayoutItem', 'config'],
          key: name,
          blocks,
          getMergedTemplate,
          currentOverrides,
          jsxComponents,
          sortComponents,
        });
      }
      case 'LayoutItem': {
        currentItem.name = currentItem.name || defaultValue?.name;

        if (!currentItem.name) {
          return [];
        }

        return computeLayoutItem({
          friendlyName,
          name,
          currentItem,
          path: [...currentPath],
          pathPostFix: ['config'],
          blocks,
          getMergedTemplate,
          currentOverrides,
          jsxComponents,
          sortComponents,
        });
      }
      case 'Object': {
        return computeObject({
          friendlyName,
          name,
          keys,
          currentItem,
          currentPath: [...currentPath, name],
          renderCallback: computeChildren,
          key: name,
        });
      }
      case 'SortableObject': {
        return computeSortableObject({
          name: friendlyName || name,
          keys,
          currentItem,
          currentPath: [...currentPath, name],
          renderCallback: computeChildren,
          key: name,
          sortComponents,
        });
      }
      default: {
        return [];
      }
    }
  };

  var result = [];

  componentMetaData.params.forEach(({ name, friendlyName, type, keys }) => {
    result.push(...computeChildren({ name, friendlyName, type, keys, currentItem: layoutItem[name], currentPath: path }));
  });

  result = flattenBody(result);

  if (result.length > 0) {
    return result;
  }

  return null;
};

export default GetHierarchyParams;